import React from "react";
import { Clock, Users, UtensilsCrossed, MapPin, ArrowUpRight } from "lucide-react";
import { useSite } from "../context/SiteContext";
import useReveal from "../hooks/useReveal";
import Crest from "./Crest";

function DishCard({ dish, index }) {
  return (
    <article
      className="reveal border border-[var(--c-border)] bg-ink2 overflow-hidden flex flex-col"
      style={{ animationDelay: `${(index % 2) * 100}ms` }}
    >
      <div className="img-zoom relative h-60">
        <img src={dish.image} alt={dish.name} className="w-full h-full object-cover" />
        <span className="absolute top-4 left-4 bg-gold text-ink font-label text-[0.6rem] uppercase tracking-[0.16em] px-3 py-1.5">
          {dish.tag}
        </span>
      </div>
      <div className="p-7 flex-1 flex flex-col">
        <h3 className="font-display text-3xl font-medium text-parchment leading-tight">{dish.name}</h3>
        <p className="mt-3 text-lg leading-relaxed text-parchment/65 font-light">{dish.intro}</p>

        <div className="flex flex-wrap gap-5 mt-5 text-stone2">
          <span className="inline-flex items-center gap-1.5 font-label text-[0.62rem] uppercase tracking-[0.14em]">
            <Clock className="w-3.5 h-3.5 text-gold" /> {dish.time}
          </span>
          <span className="inline-flex items-center gap-1.5 font-label text-[0.62rem] uppercase tracking-[0.14em]">
            <Users className="w-3.5 h-3.5 text-gold" /> {dish.serves}
          </span>
        </div>

        <div className="grid sm:grid-cols-2 gap-8 mt-7 border-t border-[var(--c-border)] pt-6">
          <div>
            <h4 className="font-label text-[0.62rem] uppercase tracking-[0.2em] text-gold mb-3">Ingredients</h4>
            <ul className="space-y-1.5">
              {dish.ingredients.map((it, i) => (
                <li key={i} className="text-[0.95rem] leading-snug text-parchment/70 font-light flex gap-2">
                  <span className="text-gold">·</span> {it}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-label text-[0.62rem] uppercase tracking-[0.2em] text-gold mb-3">Method</h4>
            <ol className="space-y-2.5">
              {dish.steps.map((st, i) => (
                <li key={i} className="text-[0.95rem] leading-snug text-parchment/70 font-light flex gap-3">
                  <span className="font-display text-gold text-lg leading-none">{i + 1}</span> {st}
                </li>
              ))}
            </ol>
          </div>
        </div>
        {dish.credit && (
          <p className="mt-5 font-label text-[0.5rem] uppercase tracking-[0.12em] text-parchment/40">
            {dish.credit}
          </p>
        )}
      </div>
    </article>
  );
}

function ProductCard({ product, index }) {
  return (
    <article
      data-testid={`market-product-${product.id}`}
      className="reveal border border-[var(--c-border)] bg-ink2 overflow-hidden flex flex-col group hover:border-gold/50 transition-colors duration-300"
      style={{ animationDelay: `${(index % 3) * 100}ms` }}
    >
      <div className="img-zoom relative h-60">
        <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
        {product.badge && (
          <span className="absolute bottom-4 right-4 w-16 h-16 rounded-full overflow-hidden border border-gold/50 bg-ink/80 backdrop-blur-sm shadow-lg">
            <img src={product.badge} alt={`${product.name} emblem`} className="w-full h-full object-cover" />
          </span>
        )}
      </div>
      <div className="p-7 flex-1 flex flex-col">
        <span className="font-label text-[0.58rem] uppercase tracking-[0.2em] text-gold">{product.maker}</span>
        <h3 className="font-display text-3xl font-medium text-parchment leading-tight mt-1.5">{product.name}</h3>
        <span className="font-label text-[0.6rem] uppercase tracking-[0.14em] text-parchment/50 mt-2">{product.kind}</span>
        <p className="mt-4 text-[1.02rem] leading-relaxed text-parchment/65 font-light flex-1">{product.text}</p>
        <span className="mt-5 inline-flex w-fit items-center gap-2 border border-gold/40 px-3 py-1.5 font-label text-[0.58rem] uppercase tracking-[0.16em] text-gold">
          <MapPin className="w-3 h-3" /> {product.availability}
        </span>
        {product.link && (
          <a
            href={product.link.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`market-link-${product.id}`}
            className="mt-3 inline-flex w-fit items-center gap-1.5 text-sm text-gold hover:text-parchment transition-colors font-light"
          >
            {product.link.label}
            <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
    </article>
  );
}

export default function RecipesPage() {
  useReveal();
  const { c } = useSite();
  const { recipes } = c;
  const eraOrder = { "Stone Age": 0, Neolithic: 1, "Bronze Age": 2 };
  const sortedDishes = [...recipes.dishes].sort(
    (a, b) => (eraOrder[a.tag] ?? 3) - (eraOrder[b.tag] ?? 3)
  );
  return (
    <div className="bg-ink">
      <section className="pt-36 sm:pt-44 pb-16 border-b border-[var(--c-border)]">
        <div className="max-w-[1220px] mx-auto px-5 sm:px-8">
          <div className="flex items-center gap-3 mb-6 text-gold">
            <UtensilsCrossed className="w-4 h-4" />
            <span className="eyebrow">{recipes.label}</span>
          </div>
          <h1 className="font-display text-5xl sm:text-7xl font-light leading-[0.98] text-parchment max-w-3xl">
            {recipes.title}
          </h1>
          <p className="mt-7 text-lg sm:text-xl leading-relaxed text-parchment/65 font-light max-w-2xl">
            {recipes.intro}
          </p>
        </div>
      </section>

      {recipes.market && (
        <section className="border-b border-[var(--c-border)] bg-ink2 py-16 sm:py-24" data-testid="market-section">
          <div className="max-w-[1220px] mx-auto px-5 sm:px-8">
            <div className="max-w-2xl">
              <span className="eyebrow" data-testid="market-eyebrow">{recipes.market.eyebrow}</span>
              <h2 className="font-display text-3xl sm:text-5xl font-light leading-tight text-parchment mt-4">
                {recipes.market.heading}
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-parchment/65 font-light">
                {recipes.market.intro}
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              {recipes.market.products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {recipes.bronzeAge && (
        <section className="border-b border-[var(--c-border)] py-16 sm:py-24" data-testid="bronze-table-section">
          <div className="max-w-[1220px] mx-auto px-5 sm:px-8">
            <div className="max-w-3xl">
              <span className="eyebrow" data-testid="recipes-bronze-eyebrow">{recipes.bronzeAge.eyebrow}</span>
              <h2 className="font-display text-3xl sm:text-5xl font-light leading-tight text-parchment mt-4">
                {recipes.bronzeAge.heading}
              </h2>
            </div>
            <div className="mt-12 space-y-12 lg:space-y-16">
              {recipes.bronzeAge.body.map((p, i) => {
                const img = recipes.bronzeAge.foodImages && recipes.bronzeAge.foodImages[i];
                if (img) {
                  const flip = i % 2 === 1;
                  return (
                    <div key={i} className="reveal grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
                      <figure className={`img-zoom border border-[var(--c-border)] bg-ink2 ${flip ? "lg:order-2" : ""}`}>
                        <img src={img.src} alt={img.caption} className="w-full h-64 sm:h-80 object-cover" />
                        <figcaption className="px-5 py-4 border-t border-[var(--c-border)]">
                          <p className="font-display italic text-lg text-parchment/85 leading-snug">{img.caption}</p>
                        </figcaption>
                      </figure>
                      <p className={`text-[1.05rem] leading-relaxed text-parchment/70 font-light ${flip ? "lg:order-1" : ""}`}>{p}</p>
                    </div>
                  );
                }
                return (
                  <p key={i} className="reveal text-[1.05rem] leading-relaxed text-parchment/70 font-light max-w-3xl">{p}</p>
                );
              })}
            </div>
            <div className="mt-12 border-t border-[var(--c-border)] pt-8">
              <span className="font-label text-[0.56rem] uppercase tracking-[0.18em] text-gold">{recipes.bronzeAge.sourcesLabel}</span>
              <div className="mt-3 flex flex-col gap-1.5">
                {recipes.bronzeAge.sources.map((s, i) => (
                  <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" className="text-sm text-parchment/70 hover:text-gold transition-colors font-light w-fit">
                    {s.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="py-16 sm:py-24" data-testid="recipes-section">
        <div className="max-w-[1220px] mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-6">
            {sortedDishes.map((d, i) => (
              <DishCard key={d.id} dish={d} index={i} />
            ))}
          </div>
          <div className="mt-14 flex items-start gap-4 border-t border-[var(--c-border)] pt-8">
            <Crest size={36} className="shrink-0 opacity-70" />
            <p className="text-sm text-parchment/45 font-light leading-relaxed max-w-3xl italic">
              {recipes.note}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
